import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card } from "../ui/card";
import { toast } from "sonner";
import { HardHat, Mail, Lock, ArrowRight, PaintBucket } from "lucide-react";
import { adminApi } from "../../lib/adminApi";

export const AdminLogin = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error("Please enter email and password");
      return;
    }

    setIsLoading(true);
    try {
      const response = await adminApi.post("/admin/login", formData);
      const { admin_name } = response.data;

      toast.success(`Welcome back, ${admin_name}!`);
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-navy flex items-center justify-center p-6">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      <Card className="relative w-full max-w-md bg-white rounded-sm p-8 shadow-2xl">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 bg-brand-navy rounded-sm flex items-center justify-center">
            <PaintBucket className="w-7 h-7 text-brand-orange" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-brand-navy uppercase">
              Roll Orlando
            </h1>
            <p className="text-xs text-brand-slate tracking-widest uppercase">
              Admin Portal
            </p>
          </div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email" className="text-brand-navy font-medium">
              Email Address
            </Label>
            <div className="relative mt-2">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-slate" />
              <Input
                id="email"
                type="email"
                data-testid="admin-email"
                placeholder="admin@yourdomain.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10 h-12 border-border focus:border-brand-orange"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-brand-navy font-medium">
              Password
            </Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-slate" />
              <Input
                id="password"
                type="password"
                data-testid="admin-password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="pl-10 h-12 border-border focus:border-brand-orange"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            data-testid="admin-login-btn"
            className="w-full h-12 bg-brand-orange text-white font-heading font-bold uppercase tracking-wide hover:bg-brand-orange/90"
          >
            {isLoading ? "Signing in..." : "Sign In"}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center text-sm text-brand-slate mt-8">
          Protected admin area. Authorized personnel only.
        </p>
      </Card>
    </div>
  );
};
